import { Component, AfterViewInit, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, Input, HostListener, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import { Pluviometers } from '../../../shared/constants/pluviometer.constant';
import { bikeIcon, co2SensorIcon, currentLocationIcon, levelSensorBlueIcon, levelSensorIcon, panelIcon, pluviometerIcon, restrictionIcon, sensorIcon, soundSensorIcon, trashIcon } from '../../../shared/constants/icons.constant';
import { CommonModule, NgFor } from '@angular/common';
import { getBaseSensorPopUp, getCurrentLocationPopUp, getGatewayPopUp, getGenericPanelPopUp, getGenericSensorPopUp, getPluviometerPopUp, getTrashPopUp } from '../../../shared/constants/popup.function';
import { DialogModule } from 'primeng/dialog';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { BadgeModule } from 'primeng/badge';
import { Subscription } from 'rxjs';
import { Panels } from '../../../shared/constants/panel.constant';
import { TabMenuModule } from 'primeng/tabmenu';
import { LoaderService } from '../../../shared/services/loader.service';
import { WebsocketService } from '../../../shared/services/webSocket.service';
import { EMCO2List } from '../../../shared/constants/EMCO2.constant';
import { WS302List } from '../../../shared/constants/WS302.constant';
import { EMUDLList } from '../../../shared/constants/EMUDL.constant';
import { EM310List } from '../../../shared/constants/em310.constant';

@Component({
  selector: 'app-map-sensor',
  standalone: true,
  imports: [NgFor, CommonModule, DialogModule, RouterModule, HttpClientModule, BadgeModule, TabMenuModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './map-sensor.component.html'
})
export class MapSensorComponent implements AfterViewInit, OnDestroy {

  @Input()
  public mapId!: string;

  @Input()
  public mapInfo!: any;

  @Input()
  public color!: string;

  private _map!: L.Map;
  private readonly _baseMapURl: string = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

  public _currentLocationMarker!: L.Marker;
  private _pluviometerMarkers: L.Marker[] = [];
  private _panelMarkers: L.Marker[] = [];

  public sensorBaseLists: any = {
    '/topic/em310': EM310List,
    '/topic/emco2': EMCO2List,
    '/topic/ws302': WS302List,
    '/topic/emudl': EMUDLList,
  };

  //Sensor list and markers
  private _EM310Sensors: any[] = EM310List;
  private _EM310Markers: L.Marker[] = [];
  private _CO2Sensors: any[] = EMCO2List;
  private _CO2Markers: L.Marker[] = [];
  private _WS302Sensors: any[] = WS302List;
  private _WS302Markers: L.Marker[] = [];
  private _EMUDLSensors: any[] = EMUDLList;
  private _EMUDLMarkers: L.Marker[] = [];

  private _geoJsonRestrictionLayer: any;
  private _geoJsonBikewayLayer: any;

  public checkedItemsLength: number = 0;
  public isVisible: boolean = false;
  public isMapHidden: boolean = true;
  public selectedTab: any = '';

  private _subscription: Subscription;
  public checkboxItems: any = [];

  constructor(private _webSocketService: WebsocketService,
    private _loaderService: LoaderService,
    private _cdr: ChangeDetectorRef) {
    this._subscription = new Subscription();
  }

  ngAfterViewInit() {
    this._initializeMap();
    this._initializeSensorLists();
    this._initializeSensorsAndMarkers();
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  private _initializeSensorLists(): void {
    this.mapInfo.topics.sensor.forEach((sensorTopic: string) => {
      const baseList = this.sensorBaseLists[sensorTopic] || [];
      const list = this.setElementList(sensorTopic, baseList);
      let checkboxItem: any;

      switch (sensorTopic) {
        case '/topic/em310':
          this._EM310Sensors = list;
          checkboxItem = this._getCheckboxItemById(sensorTopic);
          this.checkboxItems.push(checkboxItem);
          break;

        case '/topic/emco2':
          this._CO2Sensors = list;
          checkboxItem = this._getCheckboxItemById(sensorTopic);
          this.checkboxItems.push(checkboxItem);
          break;

        case '/topic/ws302':
          this._WS302Sensors = list;
          checkboxItem = this._getCheckboxItemById(sensorTopic);
          this.checkboxItems.push(checkboxItem);
          break;

        case '/topic/emudl':
          this._EMUDLSensors = list;
          checkboxItem = this._getCheckboxItemById(sensorTopic);
          this.checkboxItems.push(checkboxItem);
          break;
      }
    });
  }


  private _initializeSensorsAndMarkers() {
    if (this.mapInfo?.topics?.sensor) {
      this.mapInfo.topics.sensor.forEach((topic: any) => {
        let sensors = this._getList(topic);
        let icon = this._getIcon(topic);
        let markers = this._generateSensorMarkers(sensors, icon, topic);

        this._updateMarkerList(topic, markers);

        markers.forEach((marker: L.Marker) => {
          if (!this._map.hasLayer(marker)) {
            marker.addTo(this._map);
          } else {
            this._map.removeLayer(marker);
          }
        });

        if (topic === '/topic/em310') this._EM310Sensors = sensors;
        if (topic === '/topic/emco2') this._CO2Sensors = sensors;
        if (topic === '/topic/ws302') this._WS302Sensors = sensors;
        if (topic === '/topic/emudl') this._EMUDLSensors = sensors;
      });
    }
  }

  private _initializeMap(): void {
    if (this.mapInfo?.topics?.sensor || this.mapInfo?.topics?.location) {
      this._map = new L.Map(this.mapId, { zoomAnimation: true, minZoom: 12 });
      this._map.locate({ setView: true, enableHighAccuracy: true });

      this._map.on('locationfound', (e: any) => this._onLocationFound(e));
      this._map.on('locationerror', () => {
        this._setCbbaLocation();
      });

      this._map.on('load', (e: any) => this._fillMapRoutes(this.mapInfo.topics.geojson));
      this._map.on('load', (e: any) => this._fillMapLocations(this.mapInfo.topics.location, this.mapInfo.topics.sensor));

      L.tileLayer(this._baseMapURl, { attribution: '© OpenStreetMap' }).addTo(this._map);
    }
  }

  private setElementList(topic: string, baseList: any) {
    const list: any = localStorage.getItem(topic);

    return JSON.parse(list) || baseList;
  }

  private _getCheckboxItemById(id: string): any {
    let checkboxItem: any;

    switch (id) {
      case 'panel':
        checkboxItem = {
          id: 'panel',
          tabs: 'all sensor',
          label: 'Panel inteligente',
          isChecked: true,
          functions: [
            (event: any) => this.showHidePin('panel')
          ],
          icon: panelIcon.options.iconUrl,
          disabled: false,
          layers: ['panel']
        };
        break;

      case 'pluviometer':
        checkboxItem = {
          id: 'pluviometer',
          tabs: 'all sensor',
          label: 'Pluviómetro',
          isChecked: true,
          functions: [
            (event: any) => this.showHidePin('pluviometer')
          ],
          icon: pluviometerIcon.options.iconUrl,
          disabled: false,
          layers: ['pluviometer']
        };
        break;

      case '/topic/emco2':
        checkboxItem = {
          id: 'emco2',
          tabs: 'all sensor',
          label: 'Sensor CO2',
          isChecked: true,
          functions: [
            (event: any) => { }
          ],
          icon: co2SensorIcon.options.iconUrl,
          disabled: true,
          layers: ['sensor']
        };
        break;

      case '/topic/em310':
        checkboxItem = {
          id: 'em310',
          tabs: 'all sensor',
          label: 'Sensor de distancia',
          isChecked: true,
          functions: [
            (event: any) => { }
          ],
          icon: levelSensorIcon.options.iconUrl,
          disabled: true,
          layers: ['sensor']
        };
        break;

      case '/topic/ws302':
        checkboxItem = {
          id: 'ws302',
          tabs: 'all sensor',
          label: 'Sensor de sonido',
          isChecked: true,
          functions: [
            (event: any) => { }
          ],
          icon: soundSensorIcon.options.iconUrl,
          disabled: true,
          layers: ['sensor']
        };
        break;

      case '/topic/emudl':
        checkboxItem = {
          id: 'EMUDL',
          tabs: 'all sensor',
          label: 'Sensor de distancia',
          isChecked: true,
          functions: [
            (event: any) => { }
          ],
          icon: levelSensorBlueIcon.options.iconUrl,
          disabled: true,
          layers: ['sensor']
        };
        break;

      case 'location':
        checkboxItem = {
          id: 'location',
          tabs: 'all',
          label: 'Tu ubicación',
          isChecked: false,
          functions: [],
          icon: currentLocationIcon.options.iconUrl,
          disabled: true
        };
        break;
    }

    return checkboxItem;
  }

  //Overlay
  @HostListener('window:scroll', [])
  onScroll(event: Event) {
    this.isMapHidden = true;
  }

  public onClickOutside(event: any) {
    const mapElement = event.target.closest('.test-map');
    if (!mapElement) {
      this.isMapHidden = true;
      this._cdr.detectChanges();
    } else {
      this.isMapHidden = false;
      this._cdr.detectChanges();
    }
  }

  public hideOverlayMap() {
    this.isMapHidden = false;
    this._cdr.detectChanges();
  }

  //Location - navigation
  public goToCurrentLocation() {
    this._currentLocationMarker.openPopup();
    this._map.setView(this._currentLocationMarker.getLatLng());

    setTimeout(() => {
      this._map.setZoom(13);
    }, 800);

    this._cdr.detectChanges();
  }

  private _setCbbaLocation() {
    const defaultLat = -17.387962727660373;
    const defaultLong = -66.1557113167231;

    this._map.setView(new L.LatLng(defaultLat, defaultLong), 10);
  }

  //Routes
  private _fillMapRoutes(routes: string[]): void {
    routes.forEach(route => {
      switch (route) {
        case 'bikeway':
          this._loadGeoJson('bikeway', this._geoJsonBikewayLayer);
          break;

        case 'restriction':
          this._loadGeoJsonPolyline('restriction', this._geoJsonRestrictionLayer, 'Restricción vehicular');
          break;
      }
    });
  }

  private _loadGeoJson(fileName: string, layerItem: any, color: string = '#ae1857'): void {
    this._loaderService.show();
    const filePath = this._getGeoJsonFilePath(fileName);

    const routeCheckBoxItem = {
      label: this._getCheckboxName(fileName),
      isChecked: true,
      functions: [
        (event: any) => this.showHideRoute(layerItem)
      ],
      icon: this._getIcon(fileName).options.iconUrl,
      disabled: false,
      id: fileName,
      tabs: 'all interest',
      layers: [fileName]
    }

    if (filePath) {
      fetch(filePath)
        .then(response => response.json())
        .then(geoJson => {
          layerItem = L.geoJSON(geoJson, { style: { color: color } });
          this.showHideRoute(layerItem);
          this.checkboxItems.push(routeCheckBoxItem);
          this._loaderService.hide();
        })
        .catch((error: any) => {
          this._loaderService.hide();
          console.error('Error loading GeoJSON file:', error)
        });
    } else {
      this._loaderService.hide();
      console.error('Invalid GeoJSON file name:', fileName);
    }
  }

  private _getGeoJsonFilePath(fileName: string): string | null {
    switch (fileName) {
      case 'bikeway':
        return 'assets/geojson/bikeway.geojson';

      case 'restriction':
        return 'assets/geojson/restriction.geojson';

      default:
        return null;
    }
  }

  public showHideRoute(layerItem: any) {
    if (this._map.hasLayer(layerItem)) {
      this._map.removeLayer(layerItem);
    } else {
      this._map.addLayer(layerItem);
      this._map.fitBounds(layerItem.getBounds());
    }
  }

  private _loadGeoJsonPolyline(fileName: string, layerItem: any, text?: string) {
    this._loaderService.show();
    const filePath = this._getGeoJsonFilePath(fileName);

    const routeCheckBoxItem = {
      label: this._getCheckboxName(fileName),
      isChecked: true,
      functions: [
        (event: any) => this.showHideRoute(layerItem)
      ],
      icon: this._getIcon(fileName).options.iconUrl,
      disabled: false,
      id: fileName,
      tabs: 'all interest',
      layers: [fileName]
    };

    if (filePath) {
      fetch(filePath)
        .then(response => response.json())
        .then(geoJson => {
          const polygonCoordinates = [...geoJson.features[0].geometry.coordinates, geoJson.features[0].geometry.coordinates[0]];
          const polygonGeoJson: any = {
            type: "FeatureCollection",
            features: [{
              type: "Feature",
              properties: {
              },
              geometry: {
                type: "Polygon",
                coordinates: [polygonCoordinates]
              }
            }]
          };

          layerItem = L.geoJSON(
            polygonGeoJson, {
            style: geoJson.features[0].properties
          });

          if (text) {
            layerItem.bindTooltip(text).openTooltip();
          }

          this.showHideRoute(layerItem);
          this.checkboxItems.push(routeCheckBoxItem);
          this._loaderService.hide();
        })
        .catch((error: any) => {
          this._loaderService.hide();
          console.error('Error loading GeoJSON file:', error)
        });
    } else {
      this._loaderService.hide();
      console.error('Invalid GeoJSON file name:', fileName);
    }
  }

  // Markers
  private async _fillMapLocations(locations: string[], sensors: string[]): Promise<void> {
    locations.forEach(location => {
      let checkboxItem: any;

      switch (location) {
        case 'pluviometer':
          this._pluviometerMarkers = this._generateMarker('pluviometer');
          this._markLocations(this._pluviometerMarkers, 'pluviometer');
          checkboxItem = this._getCheckboxItemById('pluviometer');
          this.checkboxItems.push(checkboxItem);
          break;

        case 'panel':
          this._panelMarkers = this._generateMarker('panel');
          this._markLocations(this._panelMarkers, 'panel');
          checkboxItem = this._getCheckboxItemById('panel');
          this.checkboxItems.push(checkboxItem);
          break;
      }
    });

    // Sensors
    if (sensors?.length > 0) {
      await this._webSocketService.connect();
      try {
        sensors.forEach(sensor => this._getSensors(sensor));
      } catch (error) { }
    }

  }

  private _getMarkerList(sensorType: string) {
    switch (sensorType) {
      case 'pluviometer':
        return this._pluviometerMarkers;

      case 'location':
        return [this._currentLocationMarker];

      case 'panel':
        return this._panelMarkers;

      case '/topic/em310':
        return this._EM310Markers;

      case '/topic/emco2':
        return this._CO2Markers;

      case '/topic/ws302':
        return this._WS302Markers;

      case '/topic/emudl':
        return this._EMUDLMarkers;

      default:
        return [];
    }
  }

  private _updateMarkerList(topic: string, markerList: any[]): void {
    switch (topic) {
      case '/topic/em310':
        this._EM310Markers = markerList;
        break;

      case '/topic/emco2':
        this._CO2Markers = markerList;
        break;

      case '/topic/ws302':
        this._WS302Markers = markerList;
        break;

      case '/topic/emudl':
        this._EMUDLMarkers = markerList;
        break;
    }
  }

  private _markLocations(markerList: L.Marker[], type: any): void {
    let icon: L.Icon<any> = this._getIcon(type);

    markerList.forEach((marker: L.Marker) => {
      marker.setIcon(icon);
      marker.addTo(this._map);
    });
  }

  private _generateSensorMarkers(list: any[], icon: any, type: string): L.Marker<any>[] {
    let listMarker: L.Marker<any>[] = [];

    list
      .filter((sensor: any) => sensor.payload)
      .map((sensor: any) => {
        let coords = {
          lat: this._parseLocation(sensor?.payload?.deviceInfo?.tags?.Location)?.lat,
          lng: this._parseLocation(sensor?.payload?.deviceInfo?.tags?.Location)?.lng
        }

        let marker = new L.Marker([coords.lat, coords.lng]);
        (marker as any).options.payload = sensor?.payload;
        marker.setIcon(icon);
        marker.bindPopup(this._getPopUpContent(type, sensor));

        listMarker.push(marker);
      });

    return listMarker;
  }

  private _generateMarker(type: string, hasPopUp: boolean = true): L.Marker<any>[] {
    let list: L.Marker<any>[] = [];
    let elementList = this._getList(type);

    elementList.forEach((element: any) => {
      let marker = new L.Marker([element.location.coords.lat, element.location.coords.lng]);

      if (hasPopUp) {
        marker.bindPopup(this._getPopUpContent(type, element));
      }

      list.push(marker);
    });

    return list;
  }

  // Sensors
  private _getSensors(topic: string): void {
    this._webSocketService.subscribeToTopic(topic, (data: any) => {
      this.updateSensorInfo(topic, JSON.parse(data));
    });
  }

  private updateSensorInfo(topic: string, sensorData: any) {
    let elementList = this._getList(topic);
    let elementListMarker: any = this._getMarkerList(topic);
    let icon: any = this._getIcon(topic);

    const sensorIndex = elementList.findIndex((sensor: any) => sensor.deviceName === sensorData.deviceInfo.deviceName);

    if (sensorIndex > -1) {
      elementList[sensorIndex].payload = sensorData;
      this._updateSensorList(topic, elementList);

      const markerIndex = elementListMarker.findIndex((m: L.Marker) => {
        return (m as any).options.payload.deviceInfo.deviceName === sensorData.deviceInfo.deviceName;
      });

      if (markerIndex > -1) {
        this._map.removeLayer(elementListMarker[markerIndex]);
        elementListMarker[markerIndex] = this._createSensorMarker(sensorData, icon, topic);
        this._map.addLayer(elementListMarker[markerIndex]);
      } else {
        const newMarker = this._createSensorMarker(sensorData, icon, topic);
        elementListMarker.push(newMarker);
        this._map.addLayer(newMarker);
      }

      this._updateMarkerList(topic, elementListMarker);
      localStorage.setItem(topic, JSON.stringify(this._getList(topic)));
    }
  }

  private _createSensorMarker(sensorData: any, icon: any, topic: string): L.Marker {
    const marker = L.marker(
      [
        this._parseLocation(sensorData?.deviceInfo?.tags?.Location)?.lat,
        this._parseLocation(sensorData?.deviceInfo?.tags?.Location)?.lng
      ], {
      icon: icon
    });

    marker.bindPopup(this._getPopUpContent(topic, { payload: sensorData }));
    (marker as any).options.payload = sensorData;

    return marker;
  }

  private _updateSensorList(topic: string, sensorList: any[]): void {
    switch (topic) {
      case '/topic/em310':
        this._EM310Sensors = sensorList;
        break;

      case '/topic/emco2':
        this._CO2Sensors = sensorList;
        break;

      case '/topic/ws302':
        this._WS302Sensors = sensorList;
        break;

      case '/topic/emudl':
        this._EMUDLSensors = sensorList;
        break;
    }
  }

  // Utils
  private _getList(type: string): any[] {
    let elementList: any[] = [];

    switch (type) {
      case 'pluviometer':
        elementList = Pluviometers;
        break;

      case 'panel':
        elementList = Panels;
        break;

      case '/topic/emco2':
        elementList = this._CO2Sensors;
        break;

      case '/topic/em310':
        elementList = this._EM310Sensors;
        break;

      case '/topic/ws302':
        elementList = this._WS302Sensors;
        break;

      case '/topic/emudl':
        elementList = this._EMUDLSensors;
        break;
    }

    return elementList;
  }

  private _getIcon(sensorType: string): L.Icon<any> {
    let icon: L.Icon<any> = new L.Icon({});

    switch (sensorType) {
      case 'pluviometer':
        icon = pluviometerIcon;
        break;

      case '/topic/em310':
        icon = levelSensorIcon;
        break;

      case '/topic/emco2':
        icon = co2SensorIcon;
        break;

      case '/topic/ws302':
        icon = soundSensorIcon;
        break;

      case '/topic/emudl':
        icon = levelSensorBlueIcon;
        break;

      case 'trash':
        icon = trashIcon;
        break;

      case 'bikeway':
        icon = bikeIcon;
        break;

      case 'restriction':
        icon = restrictionIcon;
        break;

      case 'panel':
        icon = panelIcon;
        break;
    }

    return icon;
  }

  private _getCheckboxName(type: string): string {
    let name: string = '';

    switch (type) {
      case 'bikeway':
        name = 'Ciclovía';
        break;

      case 'restriction':
        name = 'Restric. vehicular';
        break;
    }

    return name;
  }

  private _onLocationFound(e: any): void {
    let checkboxItem = this._getCheckboxItemById('location');

    this._currentLocationMarker = L.marker([e.latlng.lat, e.latlng.lng]);
    this._currentLocationMarker.setIcon(currentLocationIcon);
    this._currentLocationMarker.addTo(this._map).bindPopup(this._getPopUpContent('currentLocation', {}));
    this._setCbbaLocation();
    this.checkboxItems.splice(0, 0, checkboxItem);
  }

  public showHidePin(type: string) {
    let elementList: any = this._getMarkerList(type);

    elementList.forEach((marker: L.Marker) => {
      if (!this._map.hasLayer(marker)) {
        marker.addTo(this._map);
      } else {
        this._map.removeLayer(marker);
      }
    });
  }

  public execCheckboxFunctions(index: number) {
    this.checkboxItems[index].isChecked = !this.checkboxItems[index].isChecked;
    this.countCheckedItems();
    this.checkboxItems[index].functions.forEach((fn: Function) => fn());
  }

  public closeDialog() {
    this.isVisible = !this.isVisible;
    this._cdr.detectChanges();
  }

  public countCheckedItems(): void {
    this.checkedItemsLength = this.checkboxItems?.reduce((count: any, item: any) => count + (item.isChecked ? 1 : 0), 0);
    this._cdr.markForCheck();
  }

  private _parseLocation(location: string): { lat: number; lng: number } {
    const defaultCoords = { lat: -17.39489154204724, lng: -66.1599819897484 };

    if (!location) return defaultCoords;

    const [lat, lng] = location.split(',').map(coord => parseFloat(coord.trim()));

    return (!isNaN(lat) && !isNaN(lng)) ? { lat, lng } : defaultCoords;
  }

  private _getPopUpContent(type: string, item: any): L.Popup {
    const popUp: L.Popup = L.popup();
    let content: string = '';

    switch (type) {
      case 'pluviometer':
        content = getPluviometerPopUp(item);
        break;

      case 'gateway':
        content = getGatewayPopUp(item);
        break;

      case 'trash':
        content = getTrashPopUp(item);
        break;

      case 'hospital':
        content = getTrashPopUp(item);
        break;

      case 'police':
        content = getTrashPopUp(item);
        break;

      case 'currentLocation':
        content = getCurrentLocationPopUp();
        break;

      case 'sensor':
        content = getGenericSensorPopUp(item);
        break;

      case '/topic/em310':
        content = getBaseSensorPopUp(item, '/topic/em310');
        break;

      case '/topic/emco2':
        content = getBaseSensorPopUp(item, '/topic/emco2');
        break;

      case '/topic/ws302':
        content = getBaseSensorPopUp(item, '/topic/ws302');
        break;

      case '/topic/emudl':
        content = getBaseSensorPopUp(item, '/topic/emudl');
        break;

      case 'panel':
        content = getGenericPanelPopUp(item);
        break;
    }

    popUp.setContent(content);
    return popUp;
  }
}
